import { action, observable, makeObservable } from 'mobx';
import { type IkeClientService, Services } from './index';

const PANORAMA_OPTIONS: google.maps.StreetViewPanoramaOptions = {
	addressControl: false,
	fullscreenControl: false,
} as const;

export class StreetviewService implements IkeClientService {
	__priority = 100;

	private streetviewEl: HTMLDivElement;

	private streetviewInstance: google.maps.StreetViewPanorama;

	private streetviewServiceInstance: google.maps.StreetViewService;

	private callbackQueue: ((panorama?: google.maps.StreetViewPanorama) => void)[] = [];

	@observable isReady = false;
	@observable streetviewOptions: google.maps.StreetViewPanoramaOptions | null = null;

	constructor() {
		makeObservable(this);
	}

	init() {
		const waitForGoogleMaps = Services.Window.window.setInterval(() => {
			if (typeof google === 'undefined') {
				return;
			}

			Services.Window.window.clearTimeout(waitForGoogleMaps);

			this.setIsReady();
			this.callbackQueue.forEach((callback) => {
				callback(this.instance);
			});
		}, 10);
	}

	/**
	 * Fetch Google Map instance, reusing existing instance to avoid impacting Google Maps API quota.
	 */
	get instance(): google.maps.StreetViewPanorama {
		if (!this.isReady) {
			throw new Error('Cannot fetch map before Google Maps API loaded');
		}

		if (this.streetviewInstance == null) {
			this.streetviewEl = Services.Window.document.createElement('div');
			this.streetviewEl.style.width = '100%';
			this.streetviewEl.style.height = '100%';

			this.streetviewInstance = new google.maps.StreetViewPanorama(this.streetviewEl, this.streetviewOptions);
		}

		return this.streetviewInstance;
	}

	/**
	 * Fetch StreetViewService instance, initializing it if necessary.
	 */
	get streetViewService(): google.maps.StreetViewService {
		if (!this.streetviewServiceInstance) {
			this.streetviewServiceInstance = new google.maps.StreetViewService();
		}
		return this.streetviewServiceInstance;
	}

	attach(el: HTMLElement) {
		const instance = this.instance;

		if (!instance) {
			return null;
		}

		el.appendChild(this.streetviewEl);

		return instance;
	}

	onReady(callback: (panorama?: google.maps.StreetViewPanorama) => void) {
		this.callbackQueue.push(callback);
	}

	@action setPanoramaOptions(options: google.maps.StreetViewPanoramaOptions, patch = false) {
		if (patch) {
			this.streetviewOptions = {
				...PANORAMA_OPTIONS,
				...this.streetviewOptions,
				...options,
			};
		} else {
			this.streetviewOptions = {
				...PANORAMA_OPTIONS,
				...options,
			};
		}
	}

	@action private setIsReady() {
		this.isReady = true;
	}
}
